type FaceTecCustomization = Record<string, Record<string, unknown>>;

type FaceTecSDKWithCustomization = {
  FaceTecCustomization: new () => FaceTecCustomization;
  FaceTecCancelButtonLocation: { TopRight: unknown };
  setCustomization(customization: FaceTecCustomization): void;
};

// The FaceTec SDK renders its own overlay outside React and cannot read CSS
// variables, so the design tokens are resolved to plain values here.
// ponytail: snapshot taken when the session starts, so switching light/dark
// mid-session does not restyle the SDK. Upgrade path is re-running
// `applyFaceTecTheme` on theme change, which the SDK only allows between
// sessions anyway.
const readToken = (name: string, fallback: string) =>
  getComputedStyle(document.body).getPropertyValue(name).trim() || fallback;

// Solid dark scrim behind the camera feed: deliberately not a themed surface,
// since a light backdrop hurts framing and liveness detection.
const BACKGROUND_COLOR = '#37474F';

function buildCustomization(FaceTecSDK: FaceTecSDKWithCustomization): FaceTecCustomization {
  const customization = new FaceTecSDK.FaceTecCustomization();

  const FONT_FAMILY = readToken('--uizz-antd-font-family', "'DM Sans', sans-serif");
  const PRIMARY_COLOR = readToken('--uizz-antd-color-primary', '#2B4ACF');
  const PRIMARY_HIGHLIGHT_COLOR = readToken('--uizz-antd-color-primary-hover', PRIMARY_COLOR);

  customization.initialLoadingAnimationCustomization.messageTextColor = '#fff';

  customization.overlayCustomization.backgroundColor = BACKGROUND_COLOR;
  customization.overlayCustomization.showBrandingImage = false;
  customization.overlayCustomization.brandingImage = '';

  customization.guidanceCustomization.backgroundColors = BACKGROUND_COLOR;
  customization.guidanceCustomization.headerFont = FONT_FAMILY;
  customization.guidanceCustomization.readyScreenSubtextTextColor = '#fff';
  customization.guidanceCustomization.readyScreenHeaderTextColor = '#fff';
  customization.guidanceCustomization.subtextFont = FONT_FAMILY;
  customization.guidanceCustomization.buttonFont = FONT_FAMILY;
  customization.guidanceCustomization.buttonTextNormalColor = '#fff';
  customization.guidanceCustomization.buttonBackgroundNormalColor = PRIMARY_COLOR;
  customization.guidanceCustomization.buttonTextHighlightColor = '#fff';
  customization.guidanceCustomization.buttonBackgroundHighlightColor = PRIMARY_HIGHLIGHT_COLOR;
  customization.guidanceCustomization.buttonTextDisabledColor = 'rgba(0, 0, 0, 0.25)';
  customization.guidanceCustomization.buttonBackgroundDisabledColor = 'rgba(0, 0, 0, 0.04)';
  customization.guidanceCustomization.buttonBorderColor = 'transparent';
  customization.guidanceCustomization.buttonBorderWidth = '0';
  customization.guidanceCustomization.buttonCornerRadius = '0';
  customization.guidanceCustomization.readyScreenOvalFillColor = 'transparent';
  customization.guidanceCustomization.readyScreenTextBackgroundColor = '#fff';
  customization.guidanceCustomization.readyScreenTextBackgroundCornerRadius = '0';
  customization.guidanceCustomization.retryScreenImageBorderWidth = '2px';
  customization.guidanceCustomization.retryScreenImageCornerRadius = '0';
  customization.guidanceCustomization.retryScreenOvalStrokeColor = PRIMARY_COLOR;
  customization.guidanceCustomization.retryScreenSubtextTextColor = '#fff';
  customization.guidanceCustomization.retryScreenHeaderTextColor = '#fff';
  customization.guidanceCustomization.retryScreenSlideshowImages = [];
  customization.guidanceCustomization.retryScreenSlideshowInterval = '1500ms';
  customization.guidanceCustomization.enableRetryScreenSlideshowShuffle = true;

  customization.resultScreenCustomization.backgroundColors = '#fff';
  customization.resultScreenCustomization.foregroundColor = PRIMARY_COLOR;
  customization.resultScreenCustomization.messageFont = FONT_FAMILY;
  customization.resultScreenCustomization.activityIndicatorColor = PRIMARY_COLOR;
  customization.resultScreenCustomization.resultAnimationBackgroundColor = PRIMARY_COLOR;
  customization.resultScreenCustomization.resultAnimationForegroundColor = '#fff';
  customization.resultScreenCustomization.resultAnimationSuccessBackgroundImage = '';
  customization.resultScreenCustomization.resultAnimationUnsuccessBackgroundImage = '';
  customization.resultScreenCustomization.showUploadProgressBar = false;
  customization.resultScreenCustomization.uploadProgressTrackColor = 'rgba(0, 0, 0, 0.2)';
  customization.resultScreenCustomization.uploadProgressFillColor = PRIMARY_COLOR;

  customization.feedbackCustomization.shadow = 'none';
  customization.feedbackCustomization.cornerRadius = '0';
  customization.feedbackCustomization.backgroundColor = '#fff';
  customization.feedbackCustomization.textColor = PRIMARY_COLOR;
  customization.feedbackCustomization.textFont = FONT_FAMILY;

  customization.frameCustomization.backgroundColor = 'transparent';
  customization.frameCustomization.borderColor = 'transparent';
  customization.frameCustomization.borderWidth = '0';
  customization.frameCustomization.borderCornerRadius = '0';
  customization.frameCustomization.shadow = 'none';

  customization.cancelButtonCustomization.location = FaceTecSDK.FaceTecCancelButtonLocation.TopRight;
  customization.cancelButtonCustomization.hideForCameraPermissions = false;

  customization.vocalGuidanceCustomization.mode = 1;
  customization.vocalGuidanceCustomization.pleaseFrameYourFaceInTheOvalSoundFile =
    '/core-sdk/FaceTec_sounds/pleaseFrameYourFaceInTheOval.mp3';
  customization.vocalGuidanceCustomization.pleaseMoveCloserSoundFile = '/core-sdk/FaceTec_sounds/pleaseMoveCloser.mp3';
  customization.vocalGuidanceCustomization.pleaseRetrySoundFile = '/core-sdk/FaceTec_sounds/pleaseRetry.mp3';
  customization.vocalGuidanceCustomization.uploadingSoundFile = '/core-sdk/FaceTec_sounds/uploading.mp3';
  customization.vocalGuidanceCustomization.facescanSuccessfulSoundFile =
    '/core-sdk/FaceTec_sounds/facescanSuccessful.mp3';
  customization.vocalGuidanceCustomization.pleasePressTheButtonToStartSoundFile =
    '/core-sdk/FaceTec_sounds/pleasePressTheButtonToStart.mp3';

  return customization;
}

export function applyFaceTecTheme(FaceTecSDK: FaceTecSDKWithCustomization): void {
  const customization = buildCustomization(FaceTecSDK);
  FaceTecSDK.setCustomization(customization);
}
